import { Clock3, Gauge, Globe2 } from 'lucide-react'
import { imageReveal, staggerChildren } from '../animations/gsapAnimations'
import ceoPortrait from '../assets/sultan-rao-ceo.png'
import Button from '../components/ui/Button'
import PageIntro from '../components/ui/PageIntro'
import Section from '../components/ui/Section'
import { useGsapContext } from '../hooks/useGsapReveal'

function About() {
  /* This is a long letter, so the motion belongs to its broad chapters rather
     than to every paragraph. The portrait opens once and the four groups rise
     together; the reader is then left with still, comfortable long-form copy. */
  const messageRef = useGsapContext<HTMLDivElement>((scope) => {
    const portrait = scope.querySelector('[data-portrait]')
    if (portrait) imageReveal(portrait, { from: 'left' })

    staggerChildren(scope, '[data-message-group]', {
      y: 22,
      stagger: 0.1,
      delay: 0.2,
    })
  })

  return (
    <>
      <PageIntro
        eyebrow="About us"
        title="Message from the CEO"
        lede="A personal note from Sultan Rao on trust, transparent financing, and making vehicle ownership simpler."
      />

      <Section tone="ink" labelledBy="ceo-message-title">
        <h2 id="ceo-message-title" className="sr-only">
          A message from Sultan Rao, CEO of Sultan Motors
        </h2>

        <div
          ref={messageRef}
          className="grid items-start gap-12 lg:grid-cols-[minmax(280px,0.72fr)_minmax(0,1.28fr)] lg:gap-16 xl:gap-20"
        >
          <aside className="xl:sticky xl:top-28">
            <figure
              data-portrait
              className="overflow-hidden rounded-3xl border border-line bg-raised shadow-pop"
            >
              <div className="relative overflow-hidden border-b border-line-soft bg-elevated">
                <img
                  src={ceoPortrait}
                  alt="Sultan Rao, CEO of Sultan Motors"
                  width={940}
                  height={1672}
                  decoding="async"
                  fetchPriority="high"
                  className="aspect-9/16 w-full object-cover object-top"
                />
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-linear-to-t from-void/65 to-transparent"
                />
              </div>

              <figcaption className="flex items-end justify-between gap-5 p-6 sm:p-7">
                <span>
                  <strong className="block font-display text-xl font-semibold tracking-[-0.02em] text-platinum">
                    Sultan Rao
                  </strong>
                  <span className="mt-1 block text-[13px] text-steel">
                    CEO — Sultan Motors
                  </span>
                </span>
                <span aria-hidden="true" className="mb-1 h-px w-12 shrink-0 coachline" />
              </figcaption>
            </figure>
          </aside>

          <article className="max-w-[68ch] text-[15.5px] leading-[1.8] text-steel sm:text-base">
            <div
              data-message-group
              className="border-l-2 border-lime/55 pl-5 sm:pl-7"
            >
              <p className="font-display text-2xl font-semibold tracking-[-0.025em] text-platinum">
                Assalam-o-Alaikum,
              </p>
              <p className="mt-3 text-lg font-medium text-silver">
                Welcome to Sultan Motors.
              </p>
              <p className="mt-6">
                At Sultan Motors, our aim is to make vehicle ownership simple,
                affordable, secure, and accessible for everyone. We provide
                brand-new model vehicles through convenient bank leasing and
                instalment plans with complete transparency and professional
                support.
              </p>
              <p className="mt-5">
                The automobile industry is not just my profession—it is my
                passion. Cars have always been my favourite field, and I
                genuinely love this business. This passion motivates me to
                provide reliable service and the best possible solutions to
                every customer who places their trust in Sultan Motors.
              </p>
            </div>

            <div
              data-message-group
              className="mt-10 border-t border-line-soft pt-10"
            >
              <p>
                One of the key advantages of Sultan Motors is that we manage the
                entire financing process ourselves. Vehicles offered through
                Sultan Motors are financed and leased directly under Sultan
                Motors or our own authorised name. We do not depend on unrelated
                third parties to arrange vehicle financing for our customers.
              </p>
              <p className="mt-5">
                In the market, some dealers may arrange financing under a third
                person&apos;s name. This can potentially create complications
                later, particularly at the time of NOC issuance, ownership
                transfer, or final documentation if the person under whose name
                the vehicle was financed is unavailable or does not cooperate.
              </p>
              <p className="mt-5">
                At Sultan Motors, we aim to avoid such complications by keeping
                the financing process under our own control. From initial
                financing and documentation to lease completion, NOC, and final
                transfer, Sultan Motors remains directly involved and committed
                to assisting its customers.
              </p>
              <p className="mt-5">
                We arrange the vehicle financing by paying the required down
                payment to the bank, with only a reasonable and limited profit
                margin for Sultan Motors included in the arrangement. Our
                objective is not to charge unnecessary additional amounts, but
                to provide customers with a convenient, transparent, and fast
                solution.
              </p>
            </div>

            <div
              data-message-group
              className="mt-10 border-t border-line-soft pt-10"
            >
              <p>
                With the normal bank financing process, customers may sometimes
                have to wait up to approximately two months for vehicle
                delivery.
              </p>
              <p className="mt-5">
                At Sultan Motors, there is no such waiting period for available
                Ready Stock vehicles.
              </p>
              <p className="mt-5">
                You can select an available On-Floor vehicle, complete the
                required documentation and Direct Agreement, and take delivery
                from Sultan Motors within just 5 minutes.
              </p>

              <ul
                aria-label="Vehicle delivery comparison"
                className="mt-8 overflow-hidden rounded-2xl border border-line bg-raised shadow-card"
              >
                <li className="flex items-start gap-4 border-b border-line-soft p-5 sm:p-6">
                  <span className="icon-ring size-10 shrink-0">
                    <Clock3 aria-hidden="true" strokeWidth={1.7} className="size-4" />
                  </span>
                  <p className="pt-1.5 font-display text-[15px] font-semibold leading-relaxed text-silver sm:text-base">
                    Bank Financing — Wait for Vehicle Delivery
                  </p>
                </li>
                <li className="flex items-start gap-4 bg-lime-ink/55 p-5 sm:p-6">
                  <span className="icon-ring size-10 shrink-0 bg-lime text-void">
                    <Gauge aria-hidden="true" strokeWidth={1.8} className="size-4" />
                  </span>
                  <p className="pt-1.5 font-display text-[15px] font-semibold leading-relaxed text-platinum sm:text-base">
                    Sultan Motors — Ready Stock, Direct Agreement &amp; Delivery
                    Within 5 Minutes
                  </p>
                </li>
              </ul>
            </div>

            <div
              data-message-group
              className="mt-10 border-t border-line-soft pt-10"
            >
              <p>
                Whether you are looking for a Honda Civic RS Turbo, Honda City,
                Toyota Yaris, Fortuner, Suzuki Alto, Swift, or any other vehicle,
                we are committed to helping you select the right vehicle and plan
                according to your requirements and budget.
              </p>
              <p className="mt-5">
                If your budget is limited but you still wish to own a brand-new
                vehicle, Sultan Motors can assist you in arranging a suitable
                financing solution according to your financial capacity.
              </p>
              <p className="mt-5">
                We also provide guidance and assistance to customers whose
                vehicles have been repossessed by banks due to instalment
                shortages or payment issues and who need support with the
                vehicle release process.
              </p>
              <p className="mt-5">
                Customer trust, transparency, honesty, and satisfaction remain
                our highest priorities. Our relationship with our customers does
                not end with the delivery of the vehicle. We aim to remain with
                you throughout the journey—from financing and delivery to NOC
                and final transfer.
              </p>

              <blockquote className="mt-9 rounded-2xl border border-lime/25 bg-lime-ink/45 p-6 sm:p-8">
                <p className="font-display text-xl leading-snug font-semibold tracking-[-0.025em] text-platinum sm:text-2xl">
                  Sultan Motors — One Complete Solution, From Financing to
                  Delivery.
                </p>
              </blockquote>

              <div className="mt-9 flex flex-col gap-6 border-t border-line-soft pt-8 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="font-display text-2xl font-semibold tracking-[-0.025em] text-platinum">
                    Sultan Rao
                  </p>
                  <p className="mt-1 text-sm font-medium text-lime">
                    CEO — Sultan Motors
                  </p>
                  <a
                    href="https://www.sultanmotors28.com"
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 inline-flex items-center gap-2 break-all text-sm text-steel underline decoration-line underline-offset-4 transition-colors hover:text-lime"
                  >
                    <Globe2 aria-hidden="true" strokeWidth={1.7} className="size-4 shrink-0" />
                    www.sultanmotors28.com
                  </a>
                </div>

                <span aria-hidden="true" className="hidden h-px flex-1 coachline sm:block" />
              </div>

              <div className="mt-10 flex flex-wrap gap-3">
                <Button to="/home#cars" size="lg" arrow magnetic>
                  Explore ready stock
                </Button>
                <Button to="/contact" variant="ghost" size="lg">
                  Contact us
                </Button>
              </div>
            </div>
          </article>
        </div>
      </Section>
    </>
  )
}

export default About
